/**
 * Builds proactive "what's next?" copy and action chips after canvas workflows
 * (resume optimize, cover letter, application note, section edits).
 */

import i18n from '../../../i18n';
import type { AgentAction } from './useAgentStateMachine';

const t = i18n.t.bind(i18n);

export interface CanvasWorkflowSnapshot {
  jobTitle?: string;
  jobCompany?: string;
  hasResume: boolean;
  hasCoverLetter: boolean;
  hasApplicationNote: boolean;
  matchScore?: number | null;
}

/** Tweaks suggestion copy and action chips for specific completion moments. */
export interface EngagementOptions {
  /** User just clicked Apply — emphasize portal handoff + pipeline, hide Apply chip. */
  postApplyHandoff?: boolean;
  /** Application page opened in a new tab. */
  portalOpened?: boolean;
}

/** In-app destinations surfaced after workflow completions (markdown links persist in chat). */
export const WORKSPACE_HUB_LINKS = [
  { labelKey: 'agent.engagement.hub.dashboard', path: '/dashboard', defaultLabel: 'Dashboard' },
  { labelKey: 'agent.engagement.hub.myActivities', path: '/history', defaultLabel: 'My Activities — track applications' },
  { labelKey: 'agent.engagement.hub.interviewPrep', path: '/prepare-for-interview', defaultLabel: 'Prepare for Interview' },
] as const;

export interface CanvasWorkflowHandlers {
  onGenerateCoverLetter?: () => void;
  onGenerateApplicationNote?: () => void;
  onDownloadPdf?: () => void;
  onApply?: () => void;
  onFindSimilarJobs?: () => void;
  onOpenDashboard?: () => void;
  onOpenApplications?: () => void;
  onOpenInterviewPrep?: () => void;
}

function buildHubNavigationLines(): string[] {
  // Kept minimal — visual action chips below the message handle navigation
  return [];
}

export function getCanvasWorkflowSnapshot(state: {
  sections?: Array<{ title: string; content: string }> | null;
  coverLetter?: string | null;
  applicationNote?: string | null;
  job?: { title?: string; company?: string } | null;
  analysis?: { matchScore?: number } | null;
}): CanvasWorkflowSnapshot {
  const hasResume = (state.sections?.length ?? 0) > 0;
  return {
    jobTitle: state.job?.title,
    jobCompany: state.job?.company,
    hasResume,
    hasCoverLetter: Boolean(state.coverLetter?.trim()),
    hasApplicationNote: Boolean(state.applicationNote?.trim()),
    matchScore: state.analysis?.matchScore ?? null,
  };
}

export function buildWorkflowSuggestionLines(
  snap: CanvasWorkflowSnapshot,
  opts: EngagementOptions = {},
): string[] {
  const role = snap.jobTitle && snap.jobCompany
    ? `**${snap.jobTitle}** at **${snap.jobCompany}**`
    : t('agent.engagement.thisRole', 'this role');

  if (opts.postApplyHandoff) {
    const portalLines = opts.portalOpened
      ? [
          t('agent.engagement.postApply.portalOpenTitle', '**While the application page is open:**'),
          t('agent.engagement.postApply.pasteCover', '- Paste your cover letter if you copied it to the clipboard'),
          t('agent.engagement.postApply.completeForm', '- Complete and submit the form on the company site'),
          t('agent.engagement.postApply.comeBack', '- **Come back to this tab** — I\'ll ask you to confirm when you\'re done'),
        ]
      : [
          t('agent.engagement.postApply.nextOnSiteTitle', '**Next on the company site:**'),
          t('agent.engagement.postApply.openPosting', '- Open the job posting and submit your resume + cover letter'),
          t('agent.engagement.postApply.returnConfirm', '- Then return here and tell me **"I applied"** so I can track it'),
        ];

    return [
      '',
      ...portalLines,
      '',
      'Use the action cards below for your next step.',
    ];
  }

  if (snap.hasResume && snap.hasCoverLetter && snap.hasApplicationNote) {
    const scoreLine = snap.matchScore != null
      ? ` Your ATS match is **${Math.round(snap.matchScore)}%**.`
      : '';
    return [
      `You're set for ${role}.${scoreLine}`,
      '',
      'Tap an action below to continue — or tell me what to refine.',
    ];
  }

  // Brief one-liner — action cards below handle the rest
  return [
    '',
    'Use the action cards below, or tell me what you\'d like to do next.',
  ];
}

function buildHubActions(handlers: CanvasWorkflowHandlers): AgentAction[] {
  const hub: AgentAction[] = [];
  if (handlers.onOpenInterviewPrep) {
    hub.push({
      id: 'hub-interview',
      label: t('agent.engagement.actions.interviewPrep', 'Interview prep'),
      variant: 'secondary',
      onClick: handlers.onOpenInterviewPrep,
    });
  }
  if (handlers.onOpenApplications) {
    hub.push({
      id: 'hub-activities',
      label: t('agent.engagement.actions.trackApplications', 'Track applications'),
      variant: 'secondary',
      onClick: handlers.onOpenApplications,
    });
  }
  if (handlers.onOpenDashboard) {
    hub.push({
      id: 'hub-dashboard',
      label: t('agent.engagement.actions.dashboard', 'Dashboard'),
      variant: 'secondary',
      onClick: handlers.onOpenDashboard,
    });
  }
  return hub;
}

export function buildWorkflowActions(
  snap: CanvasWorkflowSnapshot,
  handlers: CanvasWorkflowHandlers,
  opts: EngagementOptions = {},
): AgentAction[] {
  const company = snap.jobCompany ?? t('agent.engagement.thisCompany', 'this company');
  const actions: AgentAction[] = [];

  if (opts.postApplyHandoff) {
    if (handlers.onFindSimilarJobs) {
      actions.push({
        id: 'wf-similar',
        label: t('agent.workflow.findSimilarJobs', 'Find similar jobs'),
        variant: 'primary',
        onClick: handlers.onFindSimilarJobs,
      });
    }
    if (snap.hasResume && handlers.onDownloadPdf) {
      actions.push({
        id: 'wf-download',
        label: t('agent.workflow.downloadResumePdf', 'Download resume PDF'),
        variant: 'secondary',
        onClick: handlers.onDownloadPdf,
      });
    }
    actions.push(...buildHubActions(handlers));
    return actions;
  }

  if (!snap.hasCoverLetter && handlers.onGenerateCoverLetter) {
    actions.push({
      id: 'wf-cover',
      label: t('agent.engagement.actions.writeCoverLetter', 'Write cover letter'),
      variant: 'primary',
      onClick: handlers.onGenerateCoverLetter,
    });
  } else if (!snap.hasApplicationNote && handlers.onGenerateApplicationNote) {
    actions.push({
      id: 'wf-app-note',
      label: t('agent.engagement.actions.writeApplicationLetter', 'Write application letter'),
      variant: 'primary',
      onClick: handlers.onGenerateApplicationNote,
    });
  }

  if (snap.hasResume && handlers.onDownloadPdf) {
    actions.push({
      id: 'wf-download',
      label: t('agent.workflow.downloadResumePdf', 'Download resume PDF'),
      variant: actions.length === 0 ? 'primary' : 'secondary',
      onClick: handlers.onDownloadPdf,
    });
  }

  if (snap.hasResume && snap.hasCoverLetter && handlers.onApply) {
    actions.push({
      id: 'wf-apply',
      label: t('agent.engagement.actions.applyTo', {
        defaultValue: 'Apply to {{company}}',
        company,
      }),
      variant: 'secondary',
      onClick: handlers.onApply,
    });
  }

  if (handlers.onFindSimilarJobs) {
    actions.push({
      id: 'wf-similar',
      label: t('agent.workflow.findSimilarJobs', 'Find similar jobs'),
      variant: 'secondary',
      onClick: handlers.onFindSimilarJobs,
    });
  }

  actions.push(...buildHubActions(handlers));
  return actions;
}

export function buildEngagementContent(
  leadLine: string,
  snap: CanvasWorkflowSnapshot,
  opts: EngagementOptions = {},
): string {
  return [leadLine, ...buildWorkflowSuggestionLines(snap, opts)].join('\n');
}

/** Shared system hint — append to every agent chat request. */
export const UNIVERSAL_ENGAGEMENT_PROMPT = `
CRITICAL ENGAGEMENT & POST-RESPONSE PROTOCOL:
1. NEVER END ON A STATIC BLOCK: No matter how comprehensive a response is, never terminate without an interactive next step.
2. NEXT-STEP SUGGESTIONS: End every response with 2–3 bold bullet suggestions. Use plain bold text — NEVER use markdown link syntax like [text](url).
   FORMAT (correct):
   - **Optimize your resume** for a role that fits this profile
   - **Find matching jobs** based on your skills
   - **Draft a cover letter** tailored to this position
   FORMAT (WRONG — never do this):
   - [Optimize your resume](agent)
   - [Find matching jobs](agent)
3. PROACTIVE GUIDANCE: Always close with a direct follow-up question. Be specific, not generic.
   GOOD: "Want me to optimize your resume for the Senior .NET Developer role at Infosys?"
   BAD: "Is there anything else I can help with?"
4. CAREER INSIGHTS: When relevant, share a quick actionable tip (1 sentence max):
   - "Pro tip: Resumes with quantified achievements get 40% more interviews."
   - "Industry insight: .NET developer demand grew 28% this quarter."
   - "Quick win: Adding a Projects section can boost your ATS score by 15 points."
`.trim();

export function hasEngagementFooter(content: string): boolean {
  return /\*\*What would you like to do next\?\*\*|use the buttons below|Would you like me to|Just type your request|Type your request, or use the buttons/i.test(
    content,
  );
}

export function buildGeneralEngagementFooter(
  snap: CanvasWorkflowSnapshot,
  opts: { profileRole?: string; isProfileAssessment?: boolean } = {},
): string {
  if (snap.hasResume || snap.hasCoverLetter || snap.hasApplicationNote) {
    return buildWorkflowSuggestionLines(snap).join('\n');
  }

  const role = snap.jobTitle || opts.profileRole || t('agent.engagement.yourTargetRole', 'your target role');

  if (opts.isProfileAssessment) {
    return [
      '',
      t('agent.engagement.nextSteps.title', '**What would you like to do next?**'),
      t('agent.engagement.suggestions.optimizeForRoleBullet', {
        defaultValue: '- **Optimize my resume** for a role that fits this profile',
      }),
      t('agent.engagement.suggestions.coverLetterBullet', '- **Draft a cover letter** tailored to my strengths'),
      t('agent.engagement.suggestions.findJobsRoleBullet', {
        defaultValue: '- **Find matching jobs** for **{{role}}**',
        role,
      }),
      '',
      t('agent.engagement.closing.profileAssessment', {
        defaultValue: 'Would you like me to tailor your resume for one of these target roles right now?',
      }),
    ].join('\n');
  }

  return [
    '',
    t('agent.engagement.nextSteps.title', '**What would you like to do next?**'),
    t('agent.engagement.suggestions.findJobsBullet', '- **Find matching jobs** based on your profile'),
    t('agent.engagement.suggestions.optimizeResumeBullet', '- **Optimize your resume** for a specific role'),
    t('agent.engagement.suggestions.coverLetterShortBullet', '- **Draft a cover letter** or application note'),
    '',
    t('agent.engagement.closing.general', {
      defaultValue: 'Would you like me to help with one of these right now?',
    }),
  ].join('\n');
}

export interface GeneralEngagementHandlers extends CanvasWorkflowHandlers {
  onFindMatchingJobs?: () => void;
  onOptimizeResume?: () => void;
  onBuildResume?: () => void;
  onCompleteProfile?: () => void;
  onWriteCoverLetter?: () => void;
  onWriteApplicationLetter?: () => void;
  onInterviewPrep?: () => void;
  onSkillAssessment?: () => void;
}

export type CapabilityId =
  | 'profile-assessment'
  | 'recommended-jobs'
  | 'optimize-resume'
  | 'cover-letter'
  | 'application-letter'
  | 'build-resume';

/**
 * Builds contextual engagement actions based on the active capability.
 * Each entry point funnels to the next logical steps — no dead ends.
 */
export function buildCapabilityEngagementActions(
  capabilityId: CapabilityId | null,
  snap: CanvasWorkflowSnapshot,
  handlers: GeneralEngagementHandlers,
  profileComplete?: boolean,
): AgentAction[] {
  const actions: AgentAction[] = [];

  switch (capabilityId) {
    case 'profile-assessment': {
      if (!profileComplete && handlers.onBuildResume) {
        actions.push({
          id: 'cap-build-resume',
          label: t('agent.engagement.actions.completeProfile', 'Complete my profile'),
          variant: 'primary',
          onClick: handlers.onBuildResume,
        });
      }
      if (handlers.onOptimizeResume) {
        actions.push({
          id: 'cap-optimize',
          label: t('agent.engagement.actions.optimizeResume', 'Optimize resume'),
          variant: actions.length === 0 ? 'primary' : 'secondary',
          onClick: handlers.onOptimizeResume,
        });
      }
      if (handlers.onFindMatchingJobs) {
        actions.push({
          id: 'cap-jobs',
          label: t('agent.engagement.actions.findMatchingJobs', 'Find matching jobs'),
          variant: 'secondary',
          onClick: handlers.onFindMatchingJobs,
        });
      }
      if (handlers.onWriteCoverLetter) {
        actions.push({
          id: 'cap-cover',
          label: t('agent.engagement.actions.writeCoverLetter', 'Write cover letter'),
          variant: 'secondary',
          onClick: handlers.onWriteCoverLetter,
        });
      }
      break;
    }

    case 'recommended-jobs': {
      if (handlers.onOptimizeResume) {
        actions.push({
          id: 'cap-optimize',
          label: t('agent.engagement.actions.optimizeForRole', 'Optimize resume for a role'),
          variant: 'primary',
          onClick: handlers.onOptimizeResume,
        });
      }
      if (handlers.onWriteCoverLetter) {
        actions.push({
          id: 'cap-cover',
          label: t('agent.engagement.actions.writeCoverLetter', 'Write cover letter'),
          variant: 'secondary',
          onClick: handlers.onWriteCoverLetter,
        });
      }
      if (handlers.onInterviewPrep) {
        actions.push({
          id: 'cap-interview',
          label: t('agent.engagement.actions.interviewPrep', 'Interview prep'),
          variant: 'secondary',
          onClick: handlers.onInterviewPrep,
        });
      }
      break;
    }

    case 'cover-letter': {
      if (handlers.onOptimizeResume) {
        actions.push({
          id: 'cap-optimize',
          label: t('agent.engagement.actions.optimizeResume', 'Optimize resume'),
          variant: 'primary',
          onClick: handlers.onOptimizeResume,
        });
      }
      if (handlers.onWriteApplicationLetter) {
        actions.push({
          id: 'cap-app-letter',
          label: t('agent.engagement.actions.writeApplicationLetter', 'Write application letter'),
          variant: 'secondary',
          onClick: handlers.onWriteApplicationLetter,
        });
      }
      if (handlers.onFindMatchingJobs) {
        actions.push({
          id: 'cap-jobs',
          label: t('agent.engagement.actions.findSimilarJobs', 'Find similar jobs'),
          variant: 'secondary',
          onClick: handlers.onFindMatchingJobs,
        });
      }
      break;
    }

    case 'application-letter': {
      if (handlers.onOptimizeResume) {
        actions.push({
          id: 'cap-optimize',
          label: t('agent.engagement.actions.optimizeResume', 'Optimize resume'),
          variant: 'primary',
          onClick: handlers.onOptimizeResume,
        });
      }
      if (handlers.onFindMatchingJobs) {
        actions.push({
          id: 'cap-jobs',
          label: t('agent.engagement.actions.findMoreJobs', 'Find more jobs'),
          variant: 'secondary',
          onClick: handlers.onFindMatchingJobs,
        });
      }
      if (handlers.onInterviewPrep) {
        actions.push({
          id: 'cap-interview',
          label: t('agent.engagement.actions.prepareForInterview', 'Prepare for interview'),
          variant: 'secondary',
          onClick: handlers.onInterviewPrep,
        });
      }
      break;
    }

    case 'build-resume': {
      if (handlers.onOptimizeResume) {
        actions.push({
          id: 'cap-optimize',
          label: t('agent.engagement.actions.optimizeForRole', 'Optimize for a specific role'),
          variant: 'primary',
          onClick: handlers.onOptimizeResume,
        });
      }
      if (handlers.onFindMatchingJobs) {
        actions.push({
          id: 'cap-jobs',
          label: t('agent.engagement.actions.findMatchingJobs', 'Find matching jobs'),
          variant: 'secondary',
          onClick: handlers.onFindMatchingJobs,
        });
      }
      if (handlers.onWriteCoverLetter) {
        actions.push({
          id: 'cap-cover',
          label: t('agent.engagement.actions.writeCoverLetter', 'Write cover letter'),
          variant: 'secondary',
          onClick: handlers.onWriteCoverLetter,
        });
      }
      break;
    }

    default:
      return buildGeneralEngagementActions(snap, handlers);
  }

  return actions.slice(0, 4);
}

export function buildGeneralEngagementActions(
  snap: CanvasWorkflowSnapshot,
  handlers: GeneralEngagementHandlers,
  opts: { isProfileAssessment?: boolean } = {},
): AgentAction[] {
  const canvasActions = buildWorkflowActions(snap, handlers, opts);
  if (canvasActions.length >= 2) return canvasActions.slice(0, 3);

  const actions: AgentAction[] = [...canvasActions];

  if (handlers.onFindMatchingJobs && !actions.some((a) => a.id.includes('similar') || a.id.includes('jobs'))) {
    actions.push({
      id: 'gen-jobs',
      label: t('agent.engagement.actions.findMatchingJobs', 'Find matching jobs'),
      variant: actions.length === 0 ? 'primary' : 'secondary',
      onClick: handlers.onFindMatchingJobs,
    });
  }

  if (handlers.onOptimizeResume && !actions.some((a) => a.id.includes('optimize') || a.id.includes('download'))) {
    actions.push({
      id: 'gen-optimize',
      label: t('agent.engagement.actions.optimizeResume', 'Optimize resume'),
      variant: actions.length === 0 ? 'primary' : 'secondary',
      onClick: handlers.onOptimizeResume,
    });
  }

  if (handlers.onGenerateCoverLetter && !actions.some((a) => a.id.includes('cover'))) {
    actions.push({
      id: 'gen-cover',
      label: t('agent.engagement.actions.writeCoverLetter', 'Write cover letter'),
      variant: actions.length === 0 ? 'primary' : 'secondary',
      onClick: handlers.onGenerateCoverLetter,
    });
  }

  return actions.slice(0, 3);
}
